import { useQuery } from '@tanstack/react-query';

import { getAppearanceSettings } from '@/services/settings';

export type AppearanceSettings = {
  appearance_setting_id: string;
  tenant_id: string;
  general: {
    show_logo: boolean;
    show_description: boolean;
    show_social_profiles: boolean;
    booking_calendar_view: 'monthly' | 'daily';
  };
  class: {
    show_image: boolean;
    show_price: boolean;
    show_duration: boolean;
    show_description: boolean;
    show_description_in_list?: boolean;
    show_available_slots: boolean;
    show_instructor_name: boolean;
    // Not sent by a backend from before the list had its own switches.
    show_image_in_list?: boolean;
    show_price_in_list?: boolean;
    show_duration_in_list?: boolean;
    show_available_slots_in_list?: boolean;
    show_instructor_name_in_list?: boolean;
  };
  class_pack: {
    show_image: boolean;
    show_price: boolean;
    show_description: boolean;
    show_description_in_list?: boolean;
    show_validity_period: boolean;
    show_formats_section: boolean;
    show_instructors_section: boolean;
    show_location_section: boolean;
    // Not sent by a backend from before the list had its own switches.
    show_image_in_list?: boolean;
    show_price_in_list?: boolean;
    show_validity_period_in_list?: boolean;
  };
  subscription: {
    show_image: boolean;
    show_price: boolean;
    show_description: boolean;
    show_description_in_list?: boolean;
    show_formats_section: boolean;
    show_instructors_section: boolean;
    show_location_section: boolean;
    // Not sent by a backend from before the list had its own switches.
    show_image_in_list?: boolean;
    show_price_in_list?: boolean;
  };
  branding: {
    background: string;
    button: string;
    heading_text: string;
    body_text: string;
    muted_text?: string;
  };
  seo?: {
    meta_title: string | null;
    meta_description: string | null;
    og_image: string | null;
    favicon: string | null;
    // Not sent by a backend from before the Home Screen app settings.
    app_icon?: string | null;
    app_name?: string | null;
    // Not sent by a backend from before the dark tab icon.
    favicon_dark?: string | null;
  };
};

export const APPEARANCE_QUERY_KEY = ['appearance_settings'];

// Long enough that a visitor moving between pages never refetches, short
// enough that a tab left open picks up a save on its next focus. Shared with
// the tenant record, which a save in the admin app changes at the same moment.
export const SAVED_SETTINGS_STALE_MS = 60_000;

export default function useAppearanceSettings() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: APPEARANCE_QUERY_KEY,
    queryFn: () => getAppearanceSettings(),
    staleTime: SAVED_SETTINGS_STALE_MS,
    refetchOnWindowFocus: true,
    // A failed lookup has to stay failed. Every page withholds its subtree
    // while this is loading, and the components inside that subtree observe
    // this same query, so on the default `retryOnMount` each of them mounting
    // refetches an errored query with no data, flips the pages back to their
    // loading state and unmounts them again. Measured: 256 fetches in five
    // seconds, the page pinned on its skeleton.
    retryOnMount: false,
  });
  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
